<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';

import NotificationBell from '@/components/NotificationBell.vue';
import { eqsApi } from '@/api/eqs';
import { useSession } from '@/auth/session';
import { useVersionCheck } from '@/composables/useVersionCheck';

const route = useRoute();
const router = useRouter();
const session = useSession();

/*
  侧栏底部：等我处理的三件事（0904 Tony：这块「现在没有太多作用」）。

  原来这里是 9 个两三个字母的格子（PM / DF / PF…）加一行「实时业务接口」。
  两个问题：
    · 格子点进去的地方和主菜单第 07 项「模块中心」是同一批入口，同一件事摆了两遍，
      而且缩成字母没人认得——认不出来的入口等于不存在；
    · 那行圆点看着像状态灯，代码里却是写死的 `<i></i>`，不接任何数据。
      **假状态灯比没有更坏**：真出故障时它照样是绿的，看的人反而被安抚住。

  换成三个真数字。为 0 的行不显示——没有的事不该占位置，也不该让人天天看见 0。
*/
const waiting = ref<{ approvals: number; reviews: number; tasks: number } | null>(null);

async function loadWaiting(): Promise<void> {
  try {
    waiting.value = await eqsApi.waitingOnMe();
  } catch {
    // 侧栏是路标，取不到数就整块不显示，不弹错打断人做事
    waiting.value = null;
  }
}

const waitingRows = computed(() => {
  const data = waiting.value;
  if (!data) return [];
  /*
    点进去必须**已经筛好**。0904 Tony:「这个进去不就是和我的工作台一模一样的内容吗」——
    三行都甩到 /my-work 全量清单，那这块就只是主菜单第 03 项的复读机，
    和被它换掉的那排字母格子犯的是同一个毛病。
  */
  return [
    { key: 'approvals', label: '待我审批', count: data.approvals, to: '/my-work?kind=approval' },
    { key: 'reviews', label: '待我评审', count: data.reviews, to: '/my-work?kind=review' },
    { key: 'tasks', label: '我的待办', count: data.tasks, to: '/my-work?kind=task' },
  ].filter((row) => row.count > 0);
});

onMounted(loadWaiting);
// 换页回来顺手刷一次：刚批完一条，数字还挂着旧的最让人犯嘀咕
watch(() => route.path, loadWaiting);
const { updateAvailable, reload } = useVersionCheck();
const navOpen = ref(false);
const signingOut = ref(false);
const userName = computed(() =>
  session.user.value?.displayName || session.user.value?.username || '当前用户',
);
/** 头像用姓名首字；中文取姓，英文取首字母 */
const userInitial = computed(() => userName.value.trim().charAt(0).toUpperCase() || '·');

/*
  顶栏版本号。原来模板里写死 "V0.4"，系统一路发到 0.4.5 它一直没动——
  用户截图报问题时照着念，反而把人带偏。现在构建时从 package.json 注入。

  后面还缀一个构建日期（MMDD）：版本号几周才动一次，同一天出四五个包是常态，
  真正能回答「你手上是哪个包」的是日期，不是 0.4.5。
  鼠标悬停给到分钟。typeof 判断是防呆——vite 的 define 是文本替换，
  不走构建（比如单测直挂组件）时这两个常量根本不存在。
*/
const pad = (value: number): string => String(value).padStart(2, '0');
const appVersion = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';
const buildRaw = typeof __APP_BUILD__ === 'string' ? __APP_BUILD__ : '';
const buildAt = new Date(buildRaw);
const buildKnown = buildRaw !== '' && !Number.isNaN(buildAt.getTime());
const versionLabel = buildKnown
  ? `V${appVersion} · ${pad(buildAt.getMonth() + 1)}${pad(buildAt.getDate())}`
  : `V${appVersion}`;
const versionTitle = buildKnown
  ? `前端版本 ${appVersion}，构建于 ${buildAt.getFullYear()}-${pad(buildAt.getMonth() + 1)}-${pad(buildAt.getDate())} ${pad(buildAt.getHours())}:${pad(buildAt.getMinutes())}`
  : `前端版本 ${appVersion}`;

/*
  整条主导航一次算完，序号最后统一按顺序发。

  上一版是「前四条写死 01-04，管理员那两条在模板里写死 05/06」，
  中间插进总监看板也写了 05——菜单里就出现两个 05，
  顺着数字扫的人看到 05 认成用户管理，等于这个入口不存在。
  序号一旦手写就迟早撞，所以改成按最终顺序生成，加菜单不用再管编号。

  谁能看到：项目总监、决策委员会、管理员。看不到的人连入口都不给——
  挂个点进去被拒的链接比没有更烦。canManageUsers 是兜底：
  浏览器里若还留着上一版的会话结构，canViewDirectorBoard 会是 undefined。
  后端该拦的照样拦，这里只决定显不显示。

  0929 Tony：「左边侧栏加到 12 项了，能否简洁一点重新排列一下」→ 分组。
  0930 Tony：「升级问题、节点汇总应该没有上下级关系吧」→ 不再把一页挂在另一页下面，分四组平级：
    工作：指挥台、我的工作、会议
    项目：项目组合、模具开发、模块中心
    跟踪：任务跟踪、节点汇总、升级问题——都是跨项目盯「哪里拖了、谁没动」，对象不同（人派的活 / 计划节点 / 升级的问题）
    管理：总监看板、用户管理、操作留痕
  之前为了压行数，节点汇总挂在任务跟踪下、升级问题挂在项目组合下、操作留痕挂在用户管理下，
  挂出来的是假的上下级。现在普通人 8 行、项目经理 9 行、管理员 12 行，四组、每组不超过三个。

  升级问题给会接到升级的人看：项目经理、有直属下属的主管、总监、管理员（后端 canViewEscalations 算，0929 Tony 定的范围）。
*/
interface NavEntry { path: string; label: string; exact?: boolean; index: string }

const navGroups = computed(() => {
  const director = session.canViewDirectorBoard.value || session.canManageUsers.value;
  const escalations = session.canViewEscalations?.value === true || director;
  const groups: Array<{ key: string; label: string; items: Array<Omit<NavEntry, 'index'>> }> = [
    {
      key: 'work',
      label: '工作',
      items: [
        { path: '/', label: '指挥台', exact: true },
        { path: '/my-work', label: '我的工作' },
        { path: '/meetings', label: '会议' },
      ],
    },
    {
      key: 'projects',
      label: '项目',
      items: [
        { path: '/portfolio', label: '项目组合' },
        // 0929：模具开发（跨项目的模具盘子、供应商名单与表现），人人可见，改的权限收在数据上
        { path: '/moulds', label: '模具开发' },
        { path: '/modules', label: '模块中心' },
      ],
    },
    {
      key: 'tracking',
      label: '跟踪',
      items: [
        // 0903：任务跟踪（人派的事）和节点汇总（计划节点）人人可见，可见性收在数据上不在菜单上
        { path: '/tasks', label: '任务跟踪' },
        { path: '/nodes', label: '节点汇总' },
        ...(escalations ? [{ path: '/escalations', label: '升级问题' }] : []),
      ],
    },
  ];
  const manage: Array<Omit<NavEntry, 'index'>> = [];
  if (director) manage.push({ path: '/director', label: '总监看板' });
  if (session.canManageUsers.value) manage.push({ path: '/admin/users', label: '用户管理' }, { path: '/audit', label: '操作留痕' });
  if (manage.length) groups.push({ key: 'manage', label: '管理', items: manage });
  let at = 0;
  return groups.map((group) => ({
    ...group,
    items: group.items.map((item): NavEntry => {
      at += 1;
      return { ...item, index: String(at).padStart(2, '0') };
    }),
  }));
});

function active(path: string, exact = false) {
  return exact ? route.path === path : route.path.startsWith(path);
}

async function signOut(): Promise<void> {
  signingOut.value = true;
  try {
    await session.logout();
    await router.replace({ name: 'login' });
  } finally {
    signingOut.value = false;
  }
}
</script>

<template>
  <a class="skip-link" href="#main-content">跳到主要内容</a>
  <!-- P1-05：手机菜单展开时的遮罩，点空白处收起 -->
  <button type="button" class="app-rail-backdrop" :class="{ 'is-open': navOpen }" aria-label="关闭菜单" @click="navOpen = false" />
  <aside class="app-rail" :class="{ 'is-open': navOpen }">
    <RouterLink class="brand" to="/" @click="navOpen = false">
      <img src="/brand/donghe-logo.png" alt="东禾智能" />
      <strong>PDM系统</strong>
    </RouterLink>
    <button type="button" class="rail-close" aria-label="关闭菜单" @click="navOpen = false">×</button>
    <nav class="main-nav" aria-label="主导航">
      <section v-for="group in navGroups" :key="group.key" class="nav-group" :aria-label="group.label">
        <h2>{{ group.label }}</h2>
        <RouterLink
          v-for="item in group.items"
          :key="item.path"
          class="nav-main"
          :to="item.path"
          :class="{ 'is-active': active(item.path, item.exact) }"
          @click="navOpen = false"
        >
          <b>{{ item.index }}</b><span>{{ item.label }}</span>
        </RouterLink>
      </section>
    </nav>
    <div v-if="waitingRows.length" class="rail-waiting">
      <span>等我处理</span>
      <RouterLink
        v-for="row in waitingRows"
        :key="row.key"
        :to="row.to"
        @click="navOpen = false"
      ><em>{{ row.label }}</em><b>{{ row.count }}</b></RouterLink>
    </div>
    <!--
      P1-05：手机顶栏放不下账号，收进菜单底部；桌面端由样式隐藏。
      1027 micro2：手册、大屏也收进来——手机顶栏只留菜单、短标题和通知。
    -->
    <div class="rail-account">
      <a href="manual.html" target="_blank" rel="noopener" @click="navOpen = false">手册</a>
      <RouterLink to="/bigscreen" @click="navOpen = false">大屏</RouterLink>
      <RouterLink to="/account" @click="navOpen = false">个人设置</RouterLink>
      <button type="button" class="topbar-action" :disabled="signingOut" @click="signOut">{{ signingOut ? '退出中…' : '退出' }}</button>
    </div>
  </aside>
  <div class="app-frame">
    <div v-if="updateAvailable" class="update-banner" role="status">
      <span>系统已更新到新版本，刷新后使用最新功能。</span>
      <button type="button" @click="reload">立即刷新</button>
    </div>
    <header class="topbar">
      <!-- 1028 Soft-Gate P3：读屏原来只念「☰」；名字固定，开没开由 aria-expanded 说（侧栏里的 × 是「关闭菜单」） -->
      <button class="menu-button" type="button" aria-label="打开导航" :aria-expanded="navOpen" @click="navOpen = !navOpen">☰</button>
      <div>
        <span>DONGHE INTELLIGENT · PDM</span>
        <strong>{{ route.meta.title }}</strong>
      </div>
      <!--
        右侧一组：先动作、再身份，中间一条分隔线；所有控件同高同圆角。
        1027 micro2：带 topbar-desktop-only 的在手机（≤900）上藏掉，从侧栏底部进；顶栏只留通知。
      -->
      <div class="topbar-meta">
        <!--
          手册跟着前端一起发布（apps/web/public/manual.html），不用再往群里发文件：
          界面改了就重新生成一次，所有人打开看到的都是当前这一版。
          用 <a> 不用 RouterLink：它是独立的静态页，不进 SPA 路由。
        -->
        <a
          class="topbar-action topbar-desktop-only"
          href="manual.html"
          target="_blank"
          rel="noopener"
          title="操作手册：按角色分四册，带系统真实截图"
        >手册</a>
        <RouterLink class="topbar-action topbar-desktop-only" to="/bigscreen" title="项目总览大屏（适合电视投屏）">大屏</RouterLink>
        <NotificationBell />
        <i class="topbar-divider" aria-hidden="true"></i>
        <span class="topbar-version" :title="versionTitle">{{ versionLabel }}</span>
        <RouterLink class="topbar-user topbar-desktop-only" to="/account" title="个人设置：改密码、看自己的角色">
          <i aria-hidden="true">{{ userInitial }}</i>{{ userName }}
        </RouterLink>
        <button class="topbar-action topbar-desktop-only" type="button" :disabled="signingOut" @click="signOut">
          {{ signingOut ? '退出中…' : '退出' }}
        </button>
      </div>
    </header>
    <main id="main-content" class="page-shell">
      <slot />
    </main>
  </div>
</template>

<style scoped>
/*
  分组后的侧栏：全局 styles.css 里 .main-nav a 是 72px 高的一格，这里改成「一组一个小标题 + 每项 42px」。
  0930 管理员 12 行、四组：1366×768 的笔记本上一屏放得下（上线前量过，见交接文档）。
*/
.nav-group { border-bottom: 1px solid var(--line); }
.nav-group h2 { margin: 0; padding: 10px 12px 4px; color: var(--muted); font: 700 11px var(--display); letter-spacing: .16em; }
.main-nav a.nav-main { height: 42px; border-bottom: 0; }
</style>
